/**
 * Las reglas de ritmo de las Guías in-app (plan §7). Viven juntas para que se lean como una tabla
 * y el orquestador las pruebe con sus valores reales.
 */

// Espera desde que carga la app antes del PRIMER disparo automático: el usuario llegó a hacer algo,
// no a que le tapen la pantalla.
export const APP_WARMUP_MS = 20 * 1000;
// Espera desde que entra a una pantalla: que termine de pintar antes de buscar el ancla.
export const SCREEN_SETTLE_MS = 1500;
// Cuánto se espera a que aparezca un ancla antes de dar el paso por perdido (`anchor_missing`).
export const TARGET_WAIT_MS = 8000;
// Mientras la pantalla declara que todavía carga (`guideLoadingAttr`, hoy sólo Informes), esa espera
// no arranca, hasta este tope desde que el paso empezó a esperar. La primera carga de Informes en una
// pestaña llegó a medir ~40 s (QA-VIS-informes-01); el tope evita esperar para siempre un loader colgado
// (review C-apr-guias-03).
export const SCREEN_LOADING_MAX_WAIT_MS = 60 * 1000;
// Lo mismo para un paso OPCIONAL, que se saltea en silencio, con la pantalla ya cargada (alguna ancla
// fija de la guía a la vista, `screenSettled`): ahí, si su elemento no aparece enseguida, es porque no
// hay datos. Mientras la pantalla carga espera `TARGET_WAIT_MS`, como un paso fijo.
export const OPTIONAL_TARGET_WAIT_MS = 1500;
// Cada cuánto se recalcula el «x de y» con una tarjeta a la vista: la pantalla de atrás puede terminar
// de cargar (y aparecer o no la fila de ejemplo) sin que el runner, que vive en `App`, se entere.
export const PROGRESS_REFRESH_MS = 700;
// Disparos automáticos por sesión de pestaña. Un recorrido de primera visita no mira este tope (sí
// cuenta para él): lo frena sólo una automática por PANTALLA por sesión (`SESSION_SCREENS_STORAGE_KEY`).
export const MAX_AUTO_PER_SESSION = 1;
// «Más tarde» / Esc.
export const SNOOZE_DAYS = 3;
// Debajo de este ancho no se dispara nada solo: un tooltip anclado en un teléfono tapa todo.
export const MIN_AUTO_VIEWPORT_PX = 768;
// Refrescar la lista de elegibles al volver a la pestaña si pasó este tiempo.
export const REFETCH_ON_FOCUS_MS = 15 * 60 * 1000;
// Guardar el paso actual en el server con este debounce (avances rápidos = un solo POST).
export const STEP_PERSIST_DEBOUNCE_MS = 500;
// Overlay en 1054 y tarjeta en 1055 (joyride le suma 1). Tiene que quedar ARRIBA de las capas fijas
// del marco que viven en 1050-1053 —el carril del Recorrido del Booster (`_sidebar-v2.scss`, 1050),
// el panel del perfil del cliente (1050-1051) y el cajón «Tus primeros pasos» (1051-1053)—, porque
// una tarjeta anclada a `sidebar-ia-booster` con el carril a la vista quedaba tapada (review
// C-GUIAS-10). Queda debajo de los desplegables transitorios del marco (1100), que tienen que poder
// taparla, y de los toasts (9999). Con un modal de Bootstrap (1055) empata, pero nada se dispara solo
// con un modal abierto.
export const GUIDE_Z_INDEX = 1054;
// Distancia al borde de arriba del contenedor que scrollea donde joyride deja el elemento iluminado.
// El topbar de la 2.1 mide 56 px y es fijo: con el default de joyride (20) el elemento quedaba
// debajo de él (review C-rec-guias-03).
export const GUIDE_SCROLL_OFFSET_PX = 88;

export const SESSION_STORAGE_KEY = 'dashbo_guides_auto_shown';
// Las pantallas (`guideScreenKey`) donde ya se mostró una automática en esta sesión de pestaña.
export const SESSION_SCREENS_STORAGE_KEY = 'dashbo_guides_auto_screens';
// Qué pantalla gastó cada guía automática al dibujarse (`{guideId: guideScreenKey}`), para que su CTA
// libere ESA y no la del render actual: el modal sin pantalla sigue al usuario si cambia de ruta, y la
// reapertura manual desde el menú puede llegar después de un F5 (review C-…-renovado-01).
export const SESSION_SCREEN_BY_GUIDE_STORAGE_KEY = 'dashbo_guides_screen_by_guide';

// La guía de bienvenida a la 2.1 (modal con el video del lanzamiento, 06/10/2026). Se crea y se edita
// en el CC; la entrada «Lo nuevo de Dashbo» del menú la reabre por este slug y se muestra sólo si
// existe y le toca al usuario (`guides/by-slug/` responde 404 si no).
export const WELCOME_GUIDE_SLUG = 'bienvenida-ux21';

// Una guía lanzada desde «Aprende Dashbo» lleva, además de `?guide=`, de dónde viene y qué tarea la
// lanzó: al terminarla o cerrarla se ofrece volver a la página con la tarea que sigue abierta (C-03 de
// la tanda del 14/10). El provider las saca de la URL junto con `guide`.
export const GUIDE_FROM_PARAM = 'guide_from';
export const GUIDE_TASK_PARAM = 'guide_task';
export const GUIDE_FROM_LEARN = 'aprende';
// A dónde se vuelve, y con qué parámetro la página sabe de qué tarea se vuelve (`LearnContent`).
export const LEARN_PATH = '/aprende';
export const LEARN_RETURN_PARAM = 'desde';
// «Volver a Aprende» espera que el backend guarde el cierre de la guía (la página decide con él qué
// recorrido falta), pero no más que esto: con la red lenta, volver importa más que el número exacto.
export const RETURN_POST_WAIT_MS = 2000;

export const MODE_AUTO = 'auto';
export const MODE_MANUAL = 'manual';
export const MODE_PREVIEW = 'preview';
